import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectFade, Navigation, Pagination } from "swiper/modules";
import { isNil, range, pullAt } from "lodash";
// import { FcCalendar } from "react-icons/fc";
import { clsx } from "clsx";
import { isMobile } from "react-device-detect";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import "./property-image-swiper.scss";

import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";

import Block2Locked from "@/assets/images/block-2-locked.jpg";
import Block3Locked from "@/assets/images/block-3-locked.jpg";
import CustomButton from "@/components/common/buttons/custom-button/custom-button";
import ScaleLoader from "react-spinners/ScaleLoader";
import AnimateFade from "@/components/animations/AnimateFade";
import useLocalStorage from "@/hooks/useLocalStorage";
import { useLiveVariant } from "@/context/LiveVariantContext";
import { PRICEME_AD_PLACEMENT_ATTR, PRICEME_AD_TYPE_ATTR } from "@/ads/priceme-ad-placement";
import {
    computeShouldRenderFullscreenStickyAd,
    FULLSCREEN_FOOTER_PORTAL_ROOT_ELEMENT_ID,
    FULLSCREEN_STICKY_FOOTER_BODY_CLASS,
    FULLSCREEN_STICKY_FOOTER_BOTTOM_SAFE_PADDING_PX,
    getFullscreenSafeAreaInsetBottomPx,
    resolveFancyboxActiveSlideImageUrl,
    STICKY_FOOTER_AD_RESERVED_HEIGHT_PX,
} from "@/ads/fullscreen-sticky-footer-layout";

const AUTOPLAY_DELAY = 2000;

interface Property {
    imageUrls?: string[];
    statusIndicator?: string;
}

interface PropertyImageSwiperProps {
    property?: Property;
    round?: number;
    blurredBlocks?: number[];
    handleRevealInfo?: (block: number) => void;
    hasUnblurredTheRound?: boolean;
    index?: number;
    onImageClick?: (index: number) => void;
    /** When true, shifts the status badge so it does not overlap a hero back control. */
    hasBackButton?: boolean;
}

const PropertyImageSwiper: React.FC<PropertyImageSwiperProps> = (props) => {
    const swiperElRef = useRef<any>(null);
    const { liveVariantState } = useLiveVariant();
    const [preferredVersion] = useLocalStorage("live-variant-preferred-version", "classic");
    const propertySwiperRef = useRef<any>(null);
    const [loaded, setLoaded] = useState(false);
    const [isFullscreenViewerOpen, setIsFullscreenViewerOpen] = useState<boolean>(false);
    const [fullscreenSafeAreaBottomInsetPx, setFullscreenSafeAreaBottomInsetPx] = useState<number>(0);
    const [fullscreenViewportSize, setFullscreenViewportSize] = useState<{ width: number; height: number }>(() => ({
        width: typeof window !== "undefined" ? window.innerWidth : 0,
        height: typeof window !== "undefined" ? window.innerHeight : 0,
    }));
    const [fullscreenActiveSlideUrl, setFullscreenActiveSlideUrl] = useState<string | null>(null);
    const [fullscreenImageAspectRatio, setFullscreenImageAspectRatio] = useState<number | null>(null);
    const [fullscreenFooterPortalRoot, setFullscreenFooterPortalRoot] = useState<HTMLElement | null>(null);
    const isPlusMinus = !isNil(props?.round);
    const statusIndicator = props?.property?.statusIndicator;
    useEffect(() => {
        if (typeof document === "undefined") {
            return;
        }
        const existing = document.getElementById(FULLSCREEN_FOOTER_PORTAL_ROOT_ELEMENT_ID);
        if (existing) {
            setFullscreenFooterPortalRoot(existing);
            return () => {};
        }
        const el = document.createElement("div");
        el.id = FULLSCREEN_FOOTER_PORTAL_ROOT_ELEMENT_ID;
        document.body.appendChild(el);
        setFullscreenFooterPortalRoot(el);
        return () => {
            el.remove();
        };
    }, []);
    // Normalize and sanitize image list (remove falsy/trim/duplicates)
    let propertyImages = Array.from(
        new Set(
            (props?.property?.imageUrls ?? [])
                .filter((u) => typeof u === "string" && u.trim().length > 0)
                .map((u) => (u as string).trim()),
        ),
    );
    // Calculate numberOfImages and halfOfImages after sanitization to ensure accuracy
    const numberOfImages = propertyImages.length;
    const halfOfImages = Math.floor(numberOfImages / 2);
    const isLiveVariant = (preferredVersion && preferredVersion !== "classic") || window.location.pathname === "/";
    const isV3Flip = preferredVersion === "flip";

    const defaultLoadedImages = [true, ...Array(Math.max((propertyImages.length ?? 0) - 1, 0)).fill(false)];

    // Start with only the first image set to load
    const [loadedImages, setLoadedImages] = useState(defaultLoadedImages);

    if (props?.round === 1) {
        // Round 1: Only show the first image
        propertyImages = [propertyImages[0]];
    } else if (isPlusMinus) {
        // Save a copy of the sanitized array before modifying it
        const sanitizedImages = [...propertyImages];
        // Initialize the array with the first image
        propertyImages = [propertyImages[0]];

        // Handle block 2 (indices from 1 to halfOfImages)
        if (!props?.blurredBlocks?.includes(2)) {
            const block2Items = pullAt([...sanitizedImages], range(1, halfOfImages));
            propertyImages = [...propertyImages, ...block2Items];
        } else {
            propertyImages.push(Block2Locked); // Add LockedImage for block 2
        }

        // Handle block 3 (indices from halfOfImages to numberOfImages)
        if (!props?.blurredBlocks?.includes(3)) {
            const block3Items = pullAt([...sanitizedImages], range(halfOfImages, numberOfImages));
            propertyImages = [...propertyImages, ...block3Items];
        } else {
            propertyImages.push(Block3Locked); // Add LockedImage for block 3
        }
    }

    const renderRevealButton = (intIndex: number, strImageUrl: string) => {
        let buttonText = "";
        let block: number | null = null;
        const isBlock2 = strImageUrl.includes("block-2-locked") && props?.blurredBlocks?.includes(2);
        const isBlock3 = strImageUrl.includes("block-3-locked") && props?.blurredBlocks?.includes(3);

        if (intIndex === 0 || props?.round === 1) {
            // Don't show button on first image or if round is 1
            return null;
        } else if (isBlock2) {
            buttonText = `Reveal photos 2-${halfOfImages}`;
            block = 2;
        } else if (isBlock3) {
            buttonText = `Reveal photos ${halfOfImages + 1}-${numberOfImages}`;
            block = 3;
        }

        // Don't render the button if there's no text
        if (!buttonText.length) {
            return null;
        }

        return (
            <CustomButton
                id="reveal-button"
                dataCy="reveal-button"
                className="reveal-btn gold-solid"
                text={buttonText}
                handleClick={(event) => {
                    event?.stopPropagation(); // Stop the click event from propagating to the image
                    props?.handleRevealInfo?.(intIndex === 2 ? 2 : block!); // For some reason image two has index of 2 instead of 1
                }}
            />
        );
    };

    /**
     * Handles the image loading state by updating the `loadedImages` array.
     * Marks the current image as loaded and allows the next image to load.
     *
     * @param {number} index - The index of the image that has loaded.
     */
    const handleImageLoad = (index: number) => {
        setLoadedImages((prev) => {
            const newLoadedImages = [...prev];
            newLoadedImages[index] = true; // Mark current image as loaded

            // Allow next image to load
            if (index + 1 < propertyImages.length) {
                newLoadedImages[index + 1] = true;
            }

            return newLoadedImages;
        });

        if (index === 1) {
            setLoaded(true); // When the second image is loaded, start the autoplay
        }
    };

    // Ensure sequence continues if an image fails to load
    const handleImageError = (index: number) => {
        setLoadedImages((prev) => {
            const newLoadedImages = [...prev];
            newLoadedImages[index] = true;
            if (index + 1 < propertyImages.length) {
                newLoadedImages[index + 1] = true;
            }
            return newLoadedImages;
        });

        if (index === 1) {
            setLoaded(true);
        }
    };

    // Set the swiper progress to 0 when the images are updated (switch property) unless the index is set to 0
    useEffect(() => {
        const propertySwiper = propertySwiperRef.current;
        if (propertySwiper && !propertySwiper.destroyed && props?.index !== 0) {
            setLoadedImages(defaultLoadedImages); // Reset the loaded images
            propertySwiper.setProgress(0);

            if (!isPlusMinus && !isV3Flip) {
                setTimeout(() => propertySwiper.autoplay?.start(), AUTOPLAY_DELAY);
            }
            setLoaded(false);
        }
    }, [props.property?.imageUrls]); // eslint-disable-line react-hooks/exhaustive-deps

    // Starts or stops swiper autoplay based on first image loading state
    useEffect(() => {
        const propertySwiper = propertySwiperRef.current;
        if (propertySwiper && !propertySwiper.destroyed) {
            if (isV3Flip && propertySwiper.realIndex === 0) {
                // to stop image slider at first slide for flip game
                propertySwiper.autoplay.stop();
                return; // Ensure no further autoplay logic executes
            }

            if (loaded) {
                let isAutoplayStopped = false;

                // Initialize the fancybox
                Fancybox.bind("[data-fancybox]", {
                    on: {
                        // When the fancybox is opened, stop the autoplay
                        "Carousel.ready": (fancybox: any) => {
                            isAutoplayStopped = true;
                            propertySwiper.autoplay.stop();
                            setIsFullscreenViewerOpen(true);
                            setFullscreenSafeAreaBottomInsetPx(getFullscreenSafeAreaInsetBottomPx());
                            setFullscreenActiveSlideUrl(resolveFancyboxActiveSlideImageUrl(fancybox));
                        },
                        "Carousel.change": (fancybox: any) => {
                            setFullscreenActiveSlideUrl(resolveFancyboxActiveSlideImageUrl(fancybox));
                        },
                        // When the fancybox is closing, stop the autoplay and slide to the last opened slide
                        shouldClose: (fancybox: any) => {
                            propertySwiper.autoplay.stop();
                            propertySwiper.slideTo(fancybox.getSlide().index);
                            setIsFullscreenViewerOpen(false);
                            setFullscreenActiveSlideUrl(null);
                            setFullscreenImageAspectRatio(null);
                        },
                    },
                    Toolbar: {
                        enabled: false,
                    },
                    Thumbs: false,
                    Carousel: {
                        // preload: 1, // Only preload next/previous images - removed due to type incompatibility
                    },
                } as any);

                setTimeout(() => {
                    if (!isAutoplayStopped && !isPlusMinus && !isV3Flip) {
                        propertySwiper.autoplay?.start();
                    }
                }, AUTOPLAY_DELAY);
            } else {
                propertySwiper.autoplay.stop();
            }
        }
    }, [loaded]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (typeof document === "undefined") {
            return;
        }
        if (!isFullscreenViewerOpen) {
            document.body.classList.remove(FULLSCREEN_STICKY_FOOTER_BODY_CLASS);
            return;
        }
        document.body.classList.add(FULLSCREEN_STICKY_FOOTER_BODY_CLASS);
        return () => {
            document.body.classList.remove(FULLSCREEN_STICKY_FOOTER_BODY_CLASS);
        };
    }, [isFullscreenViewerOpen]);

    useEffect(() => {
        if (!isFullscreenViewerOpen) {
            return;
        }
        const handleViewportResize = () => {
            setFullscreenViewportSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
            setFullscreenSafeAreaBottomInsetPx(getFullscreenSafeAreaInsetBottomPx());
        };
        handleViewportResize();
        window.addEventListener("resize", handleViewportResize);
        window.addEventListener("orientationchange", handleViewportResize);
        return () => {
            window.removeEventListener("resize", handleViewportResize);
            window.removeEventListener("orientationchange", handleViewportResize);
        };
    }, [isFullscreenViewerOpen]);

    useEffect(() => {
        if (!isFullscreenViewerOpen || !fullscreenActiveSlideUrl) {
            setFullscreenImageAspectRatio(null);
            return;
        }
        let isCancelled = false;
        const image = new Image();
        image.onload = () => {
            if (isCancelled || !image.naturalWidth || !image.naturalHeight) {
                return;
            }
            setFullscreenImageAspectRatio(image.naturalWidth / image.naturalHeight);
        };
        image.onerror = () => {
            if (!isCancelled) {
                setFullscreenImageAspectRatio(null);
            }
        };
        image.src = fullscreenActiveSlideUrl;
        return () => {
            isCancelled = true;
            image.onload = null;
            image.onerror = null;
        };
    }, [fullscreenActiveSlideUrl, isFullscreenViewerOpen]);

    const autoplayOptions = useMemo(
        () => (isV3Flip ? false : { delay: AUTOPLAY_DELAY, disableOnInteraction: true }),
        [isV3Flip],
    );
    const navigationOptions = useMemo(() => ({ prevEl: ".swiper-button-prev", nextEl: ".swiper-button-next" }), []);
    const renderFraction = useCallback(
        (currentClass: string, totalClass: string) => {
            if ((props.property?.imageUrls?.length ?? 0) < 5 && !isPlusMinus && !isLiveVariant) {
                return `<div class="pagination-fraction-wrapper px-1 insufficient-image-text" data-cy="pagination">
                            <span><i>MLS rules require us to keep the rest of these photos top secret</i></span>
                        </div>`;
            }

            return `<div class="pagination-fraction-wrapper px-1 ${isV3Flip ? "tw-h-[22px]" : ""}" data-cy="pagination">
                        <span class="${currentClass}"></span> of <span class="${totalClass}"></span>
                    </div>`;
        },
        [props.property?.imageUrls?.length, isPlusMinus, isLiveVariant, isV3Flip],
    );
    const paginationOptions = useMemo(
        () => ({ el: ".swiper-pagination", type: "fraction", renderFraction }),
        [renderFraction],
    );

    const shouldRenderFullscreenStickyAd = useMemo(
        () =>
            computeShouldRenderFullscreenStickyAd({
                isFullscreenOpen: isFullscreenViewerOpen,
                viewportWidth: fullscreenViewportSize.width,
                viewportHeight: fullscreenViewportSize.height,
                imageAspectRatio: fullscreenImageAspectRatio,
                safeAreaBottomInsetPx: fullscreenSafeAreaBottomInsetPx,
            }),
        [
            fullscreenImageAspectRatio,
            fullscreenSafeAreaBottomInsetPx,
            fullscreenViewportSize.height,
            fullscreenViewportSize.width,
            isFullscreenViewerOpen,
        ],
    );

    return (
        <>
            {isFullscreenViewerOpen &&
                shouldRenderFullscreenStickyAd &&
                fullscreenFooterPortalRoot &&
                createPortal(
                    <div
                        className="priceme-fullscreen-sticky-footer-ad tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-pointer-events-none"
                    >
                        <div
                            className="tw-mx-auto tw-max-w-[728px] tw-px-3 tw-pointer-events-auto"
                            style={{
                                paddingBottom: `${fullscreenSafeAreaBottomInsetPx + FULLSCREEN_STICKY_FOOTER_BOTTOM_SAFE_PADDING_PX}px`,
                            }}
                        >
                            <div
                                className="tw-w-full"
                                style={{ height: `${STICKY_FOOTER_AD_RESERVED_HEIGHT_PX}px` }}
                                {...{ [PRICEME_AD_PLACEMENT_ATTR]: "" }}
                                {...{ [PRICEME_AD_TYPE_ATTR]: "sticky-footer" }}
                            />
                        </div>
                    </div>,
                    fullscreenFooterPortalRoot,
                )}
            <Swiper
            ref={swiperElRef}
            modules={[Autoplay, Navigation, Pagination, EffectFade]}
            autoplay={autoplayOptions as any}
            loop={!isV3Flip}
            navigation={navigationOptions as any}
            pagination={paginationOptions as any}
            className={clsx("property-image-swiper tw-relative tw-max-w-[700px]", {
                "!tw-h-[33svh] md:!tw-h-[35svh] lg:!tw-h-[33svh]": isV3Flip,
            })}
            data-cy="property-image-swiper"
            speed={400}
            effect="fade"
            onSwiper={(swiper: any) => {
                propertySwiperRef.current = swiper;

                // Dynamically add classes to the pagination element after rendering
                const paginationEl = swiper.el.querySelector(".swiper-pagination");
                if (paginationEl && isV3Flip) {
                    paginationEl.classList.add(
                        "!tw-top-4",
                        "!tw-justify-start",
                        "!tw-pl-2",
                        "md:!tw-pl-24",
                        "lg:!tw-pl-[25rem]",
                        "!tw-z-[4]",
                        "!tw-bottom-auto",
                    );
                }
            }}
        >
            {propertyImages.map((strImageUrl: string, intIndex: number) => (
                <SwiperSlide key={"property-image-slide-" + intIndex} data-cy="swiper-slide" className="swiper-slide">
                    <div
                        className="image-container d-flex justify-content-center"
                        data-fancybox="gallery"
                        data-src={strImageUrl}
                    >
                        {statusIndicator &&
                            (preferredVersion === "over-under" ||
                                preferredVersion === "social" ||
                                (preferredVersion === "puzzle" && !liveVariantState.same_with_current_date)) && (
                                <span
                                    className={clsx(
                                        "tw-absolute tw-top-[5%] tw-py-1 tw-px-4 tw-rounded-[500px]",
                                        {
                                            "tw-bg-[#AAD880]": statusIndicator.toLowerCase() === "active",
                                            "tw-bg-[#FDD56F]": statusIndicator.toLowerCase() === "contract",
                                            "tw-bg-[#D9D9D9]": statusIndicator.toLowerCase() === "closed",
                                        },
                                        props.hasBackButton
                                            ? isMobile
                                                ? "tw-left-[15%]"
                                                : "tw-left-[19%]"
                                            : isMobile
                                              ? "tw-left-[5%]"
                                              : "tw-left-[10%]",
                                    )}
                                >
                                    {statusIndicator}
                                </span>
                            )}
                        {!loadedImages[intIndex] || isNil(loadedImages[intIndex]) ? (
                            <div className="tw-w-full tw-absolute tw-bottom-[50%] tw-flex tw-justify-center tw-items-center">
                                <ScaleLoader color="#63c19f" />
                            </div>
                        ) : (
                            <AnimateFade show={loadedImages[intIndex]}>
                                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                                <a
                                    data-fancybox
                                    data-src={strImageUrl}
                                    onClick={(e) => {
                                        if (props.onImageClick) {
                                            e.preventDefault();
                                            props.onImageClick(intIndex);
                                        }
                                    }}
                                >
                                    <img
                                        alt={"property-image-slide-" + intIndex}
                                        onLoad={() => handleImageLoad(intIndex)} // Sequential loading
                                        onError={() => handleImageError(intIndex)}
                                        src={strImageUrl}
                                        style={{
                                            display: loadedImages[intIndex] ? "block" : "none",
                                            width: "100%",
                                            cursor: "pointer",
                                        }} // Only display when marked to load
                                    />
                                </a>
                            </AnimateFade>
                        )}

                        {isPlusMinus && renderRevealButton(intIndex, strImageUrl)}
                    </div>
                </SwiperSlide>
            ))}
            <div
                className="swiper-button-prev tw-absolute tw-top-1/2 -tw-translate-y-1/2 !tw-left-0 tw-z-[5]"
                data-cy="swiper-button-prev"
            ></div>
            <div
                className="swiper-button-next tw-absolute tw-top-1/2 -tw-translate-y-1/2 !tw-right-0 tw-z-[5]"
                data-cy="swiper-button-next"
            ></div>
            <div className="swiper-pagination"></div>
            </Swiper>
        </>
    );
};

export default PropertyImageSwiper;
